import { FC, HTMLProps, useContext, useMemo } from 'react';
import getConfig from 'next/config';
import Link from 'next/link';

import classNames from 'classnames';
import { StaticGoogleMap, Marker } from 'react-static-google-map';
import { i18n as I18n } from 'utils/i18n';
import { filteredKeyFacets } from 'utils/filters';

import FeatureIcon from 'components/shared/featureIcon';
import FloorplansButton from 'components/floorplansButton';

import { FavouriteButton } from 'components/shared/favouriteButton';
import useFavourite from 'hooks/useFavourite';

import { AuthContext } from 'state/auth';
import { getVenueImage } from 'utils/venueImages';

import { Venue } from 'hooks/useVenue';
import { LatLng } from 'utils/geolocation';

const { publicRuntimeConfig: { googleMapsApiKey } = {} } = getConfig();

interface Props extends HTMLProps<HTMLDivElement> {
  venue: Venue;
  location: string;
  showRooms?: boolean;
  showBookRoomsButton?: boolean;
  showFeatures?: boolean;
  floorplansButtonPosition?: 'none' | 'image' | 'feature';
  imagePosition?: 'left' | 'top';
  showMapOnThumbNail?: boolean;
  onClickMap: (latLng: LatLng) => void;
  organisationName?: string;
}

const MapVenue: FC<Props> = ({
  venue,
  location,
  showRooms = false,
  showBookRoomsButton = false,
  showFeatures = false,
  floorplansButtonPosition = 'feature',
  imagePosition = 'left',
  className: classNameProp,
  showMapOnThumbNail = false,
  onClickMap,
  organisationName,
  ...props
}) => {
  const className = classNames(
    'search-map__venue',
    {
      'search-map__venue__image-top': imagePosition === 'top',
    },
    classNameProp
  );

  const { loggedIn } = useContext(AuthContext);
  const [favourited, toggleFavourite] = useFavourite('venues', venue.id);

  const { clinicalRooms = [], nonClinicalRooms = [], facets = [] } = venue;

  const clinicalTotal = useMemo(
    () => clinicalRooms.reduce((prev, { count }) => prev + count, 0),
    [clinicalRooms]
  );
  const nonClinicalTotal = useMemo(
    () => nonClinicalRooms.reduce((prev, { count }) => prev + count, 0),
    [nonClinicalRooms]
  );

  const venueUrl = `${I18n.t('navigation.venues.url')}/${venue.id}`;

  const renderRoomList = ({ name, count }) => (
    <div key={name} className="search-map__space-rooms">
      <span className="search-map__space-quantity">{count}</span>
      <p>{name}</p>
    </div>
  );

  const venueImage = getVenueImage(
    venue,
    imagePosition === 'top' ? 'regular' : 'thumbnail'
  );
  const imageStyle = venueImage
    ? { backgroundImage: `url(${venueImage})` }
    : {};

  return (
    <div className={className} {...props}>
      <div
        className="search-map__venue-image"
        data-testid="venueImage"
        style={imageStyle}
      >
        {!venueImage && (
          <span className="search-map__no-image">
            {I18n.t('shared.picture_coming_soon')}
          </span>
        )}
        {venueImage && showMapOnThumbNail && (
          <div
            className="venue__map"
            data-testid="venueMap"
            title={I18n.t('venue_list.view_venue_on_map')}
            onClick={() => onClickMap(venue.latLng)}
          >
            {!!venue.latLng && (
              <StaticGoogleMap
                size="153x138"
                zoom={14}
                apiKey={googleMapsApiKey}
              >
                <Marker location={venue.latLng} color="blue" />
              </StaticGoogleMap>
            )}
            {!venue.latLng && (
              <div>
                Map
                <br />
                coming soon
              </div>
            )}
          </div>
        )}
        {floorplansButtonPosition === 'image' && (
          <FloorplansButton venue={venue} />
        )}
      </div>
      <div className="search-map__venue-content">
        <div className="search-map__venue-line">
          <h2 className="search-map__venue-name">
            <Link href={venueUrl}>
              <a>{venue.name}</a>
            </Link>
          </h2>

          {loggedIn && (
            <FavouriteButton
              favouritedState={favourited}
              type="venues"
              handleFavouriteButtonClick={toggleFavourite}
            />
          )}
        </div>

        <h4 className="search-map__venue-organisation">
          <span>{I18n.t('shared.managed_by')}:&nbsp;</span>
          {`${organisationName}`}
        </h4>

        {!!venue.distance && !!location && (
          <>
            <div className="search-map__venue-distance-container">
              <p
                className="search-map__venue-distance"
                data-testid="distanceLabel"
              >
                <i className="fas fa-map-marker-alt" />
                {I18n.t('search_results.miles_from_location', {
                  count: venue.distance,
                  location,
                })}
              </p>
            </div>
          </>
        )}

        <ul className="search-map__venue-space-types">
          {(!showRooms || clinicalTotal > 0) && (
            <li className="search-map__venue-space-type search-map__venue-space-type--clinical">
              <div className="search-map__space-room-types">
                {!showRooms && (
                  <span className="search-map__space-quantity">
                    {clinicalTotal}
                  </span>
                )}
                <p>clinical rooms</p>
              </div>
              {showRooms && clinicalRooms.map(renderRoomList)}
            </li>
          )}
          {(!showRooms || nonClinicalTotal > 0) && (
            <li className="search-map__venue-space-type search-map__venue-space-type--non-clinical">
              <div className="search-map__space-room-types">
                {!showRooms && (
                  <span className="search-map__space-quantity">
                    {nonClinicalTotal}
                  </span>
                )}
                <p>non-clinical rooms</p>
              </div>
              {showRooms && nonClinicalRooms.map(renderRoomList)}
            </li>
          )}
        </ul>

        <div className="search-map__venue-footer">
          {showBookRoomsButton && (
            <Link href={`${venueUrl}#venue-rooms-section`}>
              <a className="button button--primary">
                {I18n.t('venues.book_rooms')}
              </a>
            </Link>
          )}

          {showFeatures && (
            <ul className="venue__feature-list">
              {floorplansButtonPosition === 'feature' && (
                <FloorplansButton venue={venue} wrapper="li" />
              )}
              {filteredKeyFacets(facets).map(facet => (
                <li key={facet.name}>
                  <FeatureIcon {...facet} displaySvgIcon={false} />
                </li>
              ))}
            </ul>
          )}
          {!showFeatures && floorplansButtonPosition === 'feature' && (
            <FloorplansButton venue={venue} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MapVenue;
